import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { connect } from 'react-redux';

import { actions } from '../redux/actions';
import Strings from '../../../../Strings';
import { DATE_TIME_SPACE_12_UPPERCASE, TIME_FORMAT_12_UPPERCASE } from '../../../../constants';
import { Button } from '../../../../components/PageHeader/Button';
import { closeModal, openModalAction } from '../../../../actions/modal';
import { getProgramsFromPatient } from '../../../../utils/cmsPrograms';
import { DOCTOR, SUPERUSER } from '../../../../utils/userRoles';
import { toolTipForTable } from '../../../../utils';
import { EDIT_PATIENT_ACTION_MODAL_NAME, priorityLabel } from '../../../Actions/actions';

function PatientLogItem(props) {
  const {
    logItem,
    patientId,
    patientDetails,
    getNotes,
    changeActionState,
    editActionContent,
    editNoteContent,
    closeTimer,
    openChartReview,
    openCareUpdate,
    openEditAction,
    openConfirmation,
    userUUuid,
    canManageCareUpdates,
  } = props;
  const status = logItem.action_completed === true ? 'completed' : logItem.action_completed === false ? 'open' : 'note';
  const chartReviewAction = logItem.note_type === 'chart_to_review';
  const careUpdateAction = !!logItem.care_update_id;
  // Actions closable only by the system are completed and worded by their producer.
  const systemOwned = logItem.closable_by === 'system';
  const canEdit = !systemOwned && (logItem.author_uuid === userUUuid || (logItem.author_uuid == null && status !== 'note'));

  const onActionStateChange = completed => {
    if (completed && chartReviewAction) {
      closeTimer();
      setTimeout(() => {
        openChartReview({
          patientId,
          programs: getProgramsFromPatient(patientDetails),
          actionDetails: logItem,
          preActivationWorklogsEnabled: patientDetails?.patientEnrollment?.preActivationWorklogsEnabled === true,
        });
      }, 300); // Ensures timer modal is closed before opening chart review
      return;
    }
    const change = () => changeActionState(completed).then(() => getNotes());
    if (completed && logItem.closable_by === 'human_with_warning') {
      openConfirmation({
        title: Strings.patientActions.completeWarning,
        cancelText: Strings.cancel,
        confirmText: Strings.patientActions.complete,
        onConfirm: change,
      });
      return;
    }
    change();
  };

  const editNote = () => {
    if (status === 'note') editNoteContent();
    else editActionContent(logItem.content);
  };

  const dateTime = React.useMemo(() => {
    const creationTimeMoment = moment(logItem.creation_time * 1000);
    const modificationTime = logItem.modification_time && logItem.modification_time !== logItem.creation_time
    ? moment(logItem.modification_time * 1000)
    : null;
    const creationTimeFormat =
      !modificationTime
      ? TIME_FORMAT_12_UPPERCASE  
      : (modificationTime && creationTimeMoment.isSame(modificationTime, 'day'))
      ? TIME_FORMAT_12_UPPERCASE
      : DATE_TIME_SPACE_12_UPPERCASE;
    
    const dueAt = moment(logItem.due_at);

    return toolTipForTable(
      `${Strings.created} ${creationTimeMoment.format(creationTimeFormat)}${modificationTime ? ` (${Strings.edited})` : ''}`,
      `${Strings.created} ${creationTimeMoment.format(DATE_TIME_SPACE_12_UPPERCASE)}${modificationTime ? `, ${Strings.edited} ${modificationTime.format(DATE_TIME_SPACE_12_UPPERCASE)}` : ''}${logItem.due_at && logItem.status !== 'note' ? `, ${Strings.accessCareUpdates.dueDate}: ${dueAt.format(DATE_TIME_SPACE_12_UPPERCASE)}` : ''}`
    );
  }, [logItem.creation_time, logItem.modification_time]);

  const renderDueAt = (item) => {
    const dueAt = moment(item.due_at);
    // past_deadline is the server's verdict; care-update deadlines are not flagged by it yet.
    const overdue = item.action_completed === false && (item.past_deadline || dueAt.isBefore(moment()));
    return (
      <div className={`action-deadline${overdue ? ' overdue' : ''}`} data-testid="action-deadline">
        {Strings.accessCareUpdates.dueDate}: <time dateTime={item.due_at}>{dueAt.format(DATE_TIME_SPACE_12_UPPERCASE)}</time>
        {overdue && <div className="patient-action-buttons"><div className="signal-alert relative info" />{Strings.accessCareUpdates.overdue}</div>}
      </div>
    );
  };
  return (
    <div className="patient-log-logitem">
      <div className="icon-container">
        <div className={`icon ${status} ${chartReviewAction ? 'chart-to-review' : ''}`} />
      </div>
      <div className="item">
        <div className="title">
          {status === 'note' ? Strings.note : status === 'completed' ? Strings.completedAction : Strings.openAction}
          <span className="date">
            {dateTime}
          </span>
          <span style={{ paddingLeft: '5px' }}>{logItem.author_name}</span>
        </div>
        {status !== 'note' && (
          <div className="action-workflow" data-testid="action-workflow">
            {`${Strings.patientActions.priority}: ${Strings.patientActions[priorityLabel(logItem.priority)]}`}
            {logItem.assignee ? ` · ${Strings.patientActions.assignee}: ${logItem.assignee.name}` : ''}
            {status === 'completed'
              ? ` · ${logItem.action_completed_by_name
                ? Strings.formatString(Strings.patientActions.completedBy, logItem.action_completed_by_name)
                : Strings.patientActions.completedAutomatically}`
              : ''}
          </div>
        )}
        <div className="content">{logItem.content}</div>
        <div className="patient-action-buttons">
          {careUpdateAction && canManageCareUpdates && (
            <Button
              title={Strings.accessCareUpdates.open}
              onClick={() => openCareUpdate({
                patientId,
                patientDetails,
                enrollmentId: logItem.care_update_enrollment_id,
                updateId: logItem.care_update_id,
              })}
            />
          )}
          {status !== 'note' && (
            <Button
              title={Strings.patientActions.edit}
              onClick={() => openEditAction({ action: { ...logItem, patient_id: patientDetails?.masked_id }, afterSave: getNotes })}
            />
          )}
          {status === 'open' && !careUpdateAction && !systemOwned && (
            <Button
              onClick={() => onActionStateChange(true)}
              title={chartReviewAction ? Strings.review : Strings.complete}
            />
          )}
          {status === 'completed' && !chartReviewAction && !careUpdateAction && !systemOwned && (
            <Button
              onClick={() => onActionStateChange(false)}
              title={Strings.reOpen}
            />
          )}
        </div>
      </div>
      {logItem.due_at&& logItem.status !== 'note' && logItem.action_completed === false && renderDueAt(logItem)}
      {!chartReviewAction && !careUpdateAction && (
        <div
          role="button"
          aria-label={status === 'note' ? Strings.editNote : Strings.editAction}
          onClick={canEdit ? () => editNote() : undefined}
          disabled={!canEdit}
          className="edit-button"
          data-tooltip-content={status === 'note' ? Strings.editNote : Strings.editAction}
          data-tooltip-id="tooltip"
        >
          <div className="icon" />
        </div>
      )}
    </div>
  );
}

PatientLogItem.propTypes = {
  logItem: PropTypes.shape({
    content: PropTypes.string,
    creation_time: PropTypes.any,
    modification_time: PropTypes.any,
    id: PropTypes.any,
    action_completed: PropTypes.any,
    note_type: PropTypes.string,
    care_update_id: PropTypes.string,
    care_update_enrollment_id: PropTypes.string,
    author_name: PropTypes.string,
    author_uuid: PropTypes.any,
    priority: PropTypes.number,
    closable_by: PropTypes.string,
    past_deadline: PropTypes.bool,
    assignee: PropTypes.object,
    action_completed_by_name: PropTypes.string,
  }),
  patientId: PropTypes.any,
  patientDetails: PropTypes.object,
  showFullDates: PropTypes.bool,
  userUUuid: PropTypes.any,
  canManageCareUpdates: PropTypes.bool,
  openCareUpdate: PropTypes.func,
  openEditAction: PropTypes.func,
  openConfirmation: PropTypes.func,
  changeActionState: PropTypes.func,
  openChartReview: PropTypes.func,
  editContent: PropTypes.func,
  getNotes: PropTypes.func,
  closeTimer: PropTypes.func,
  openTimer: PropTypes.func,
  editNoteContent: PropTypes.func,
  editActionContent: PropTypes.func,
};

const mapDispatchToProps = (dispatch, props) => ({
  changeActionState: completed => dispatch(actions.updateActionStatus(props.logItem.id, completed)),
  editNoteContent: () =>
    dispatch(
      openModalAction('add-note', {
        noteId: props.logItem.id,
        patientId: props.patientId,
      }),
    ),
  editActionContent: () =>
    dispatch(
      openModalAction('add-note', {
        noteId: props.logItem.id,
        actionMode: true,
        patientId: props.patientId,
      }),
    ),
  getNotes: () => dispatch(actions.getNotes(props.patientId)),
  closeTimer: () => dispatch(closeModal('timer')),
  openTimer: data => dispatch(openModalAction('timer', data)),
  openChartReview: data => dispatch(openModalAction('chart-review', data)),
  openCareUpdate: data => dispatch(openModalAction('access-care-updates', data)),
  openEditAction: data => dispatch(openModalAction(EDIT_PATIENT_ACTION_MODAL_NAME, data)),
  openConfirmation: data => dispatch(openModalAction('new-confirmation-modal', data)),
});

const mapStateToProps = state => {
  return {
    userUUuid: state.auth.profile?.uuid,
    canManageCareUpdates: [DOCTOR, SUPERUSER].includes(state.auth.role),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientLogItem);
